import moment from "moment";
import React, { Component } from "react";
import { NavigateFunction, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import atividades from "../../api/atividades";
import { errorToString } from "../../helpers/funcoes";
import { Titulo } from "../Titulo";
import "./AtividadesForm.css";
import { AtividadesUploadInput } from "./AtividadesUploadInput";
import { SlotGrupo } from "./SlotGrupo";

interface SlotItem {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  resposta: string;
  grupo: number;
}

interface PageItem {
  base?: {
    uri: string;
    width: number;
    height: number;
    ref: React.RefObject<HTMLImageElement>;
  };
  mask?: {
    width: number;
    height: number;
    slots: SlotItem[];
  };
}

interface AtividadesFormState {
  audioUri?: string;
  baseUri?: string;
  baseCount: number;
  maskCount: number;
  activePage: number;
  activeSlot: number;
  pages: PageItem[];
  loadingPreview: boolean;
  loadingMask: boolean;
  loadingEntity: boolean;
  saving: boolean;
  error: string | null;
  nome: string;
  ano: number;
}

interface AtividadesFormProps {
  navigate: NavigateFunction;
  id?: string;
}

const groupColors = ["#fdffb6", "#ffd6a5", "#caffbf", "#9bf6ff"];

class AtividadesFormImpl extends Component<
  AtividadesFormProps,
  AtividadesFormState
> {
  constructor(props: AtividadesFormProps) {
    super(props);
    this.state = {
      baseCount: 0,
      maskCount: 0,
      activePage: -1,
      activeSlot: -1,
      pages: [],
      loadingPreview: false,
      loadingMask: false,
      loadingEntity: false,
      saving: false,
      error: null,
      nome: "",
      ano: 1,
    };
  }

  componentDidMount() {
    if (this.props.id) {
      this.setState({ loadingEntity: true });
      atividades
        .read(this.props.id)
        .then((res) => {
          const pdfAt = res.pdfAt ? moment(res.pdfAt).unix().toString() : "0";
          const audioAt = res.audioAt
            ? moment(res.audioAt).unix().toString()
            : "0";
          this.setState({
            loadingEntity: false,
            nome: res.nome,
            ano: res.ano,
            audioUri: res.audio ? res.audio + "?t=" + audioAt : undefined,
            activePage: 0,
            baseCount: res.pages?.length ?? 0,
            maskCount: res.pages?.length ?? 0,
            pages:
              res.pages?.map((i) => ({
                base: {
                  width: i.width,
                  height: i.height,
                  uri: i.uri + "?t=" + pdfAt,
                  ref: React.createRef(),
                  loaded: false,
                },
                mask: {
                  width: i.width,
                  height: i.height,
                  slots: i.slots.map((i) => ({
                    x1: i.coords[0],
                    y1: i.coords[1],
                    x2: i.coords[2],
                    y2: i.coords[3],
                    resposta: i.resposta,
                    grupo: i.grupo,
                  })),
                },
              })) ?? [],
          });
        })
        .catch((err) => {
          this.setState({
            error: errorToString(err),
            loadingEntity: false,
          });
        });
    }
  }

  onNomeChanged(value: string) {
    this.setState({ nome: value });
  }

  onAnoChanged(value: string) {
    this.setState({ ano: parseInt(value) });
  }

  onAudioChanged(value: string) {
    this.setState({ audioUri: value });
  }

  onBaseChanged(value: string) {
    this.setState({
      baseUri: value,
      baseCount: 0,
      loadingPreview: true,
      error: null,
    });
    atividades
      .previewPdf(value)
      .then((res) => {
        if (
          this.state.maskCount > 0 &&
          this.state.maskCount != res.pages.length
        ) {
          throw new Error(
            `O número de páginas da atividade enviada (${res.pages.length}) ` +
              `deve ser o mesmo número de páginas da máscara enviada (${this.state.maskCount}).`
          );
        }
        const pages = res.pages.map((i, idx): PageItem => {
          const p = this.state.pages[idx] ?? {};
          return {
            ...p,
            base: {
              uri: i.uri,
              width: i.width,
              height: i.height,
              ref: React.createRef(),
            },
          };
        });
        this.setState({
          pages,
          baseCount: res.pages.length,
          loadingPreview: false,
          activePage: 0,
        });
      })
      .catch((err) => {
        this.setState({
          error: errorToString(err),
          loadingPreview: false,
        });
        window.scrollTo(0, 0);
      });
  }

  onBaseLoaded(index: number) {
    if (this.state.activePage == index) {
      this.componentDidUpdate();
    }
  }

  onMaskChanged(value: string) {
    this.setState({
      loadingMask: true,
      maskCount: 0,
      error: null,
    });
    atividades
      .getMaskSlots(value)
      .then((res) => {
        if (
          this.state.baseCount > 0 &&
          this.state.baseCount != res.pages.length
        ) {
          throw new Error(
            `O número de páginas da máscara enviada (${res.pages.length}) ` +
              `deve ser o mesmo número de páginas da atividade enviada (${this.state.baseCount}).`
          );
        }
        const pages = res.pages.map((i, idx) => {
          const p = this.state.pages[idx] ?? {};
          return {
            ...p,
            mask: {
              width: i.width,
              height: i.height,
              slots: i.slots.map((j) => ({
                x1: j[0],
                y1: j[1],
                x2: j[2],
                y2: j[3],
                resposta: "",
                grupo: 0,
              })),
            },
          };
        });
        this.setState({
          pages,
          maskCount: res.pages.length,
          loadingMask: false,
          activePage: 0,
        });
      })
      .catch((err) => {
        this.setState({
          error: errorToString(err),
          loadingMask: false,
        });
        window.scrollTo(0, 0);
      });
  }

  onSlotChanged(pageIndex: number, slotIndex: number, value: string) {
    this._updateSlot(pageIndex, slotIndex, (slot) => ({
      ...slot,
      resposta: value,
    }));
  }

  onSlotGroupChanged(pageIndex: number, slotIndex: number, value: number) {
    this._updateSlot(pageIndex, slotIndex, (slot) => ({
      ...slot,
      grupo: value,
    }));
  }

  _updateSlot(
    pageIndex: number,
    slotIndex: number,
    callback: (item: SlotItem) => SlotItem
  ) {
    this.setState({
      pages: this.state.pages.map((p, pi) =>
        pageIndex != pi || !p.mask
          ? p
          : {
              ...p,
              mask: {
                ...p.mask,
                slots: p.mask.slots.map((s, si) =>
                  si == slotIndex ? callback(s) : s
                ),
              },
            }
      ),
    });
  }

  onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    this.setState({ saving: true, error: null });

    const payload = {
      nome: this.state.nome,
      ano: this.state.ano,
      audio: this.state.audioUri?.replace(/\?t=\d+$/, ""),
      pdf: this.state.baseUri,
      pages: this.state.pages.map((p) => ({
        width: p.mask?.width ?? 0,
        height: p.mask?.height ?? 0,
        slots:
          p.mask?.slots.map((i) => ({
            coords: [i.x1, i.y1, i.x2, i.y2],
            resposta: i.resposta ?? "",
            grupo: i.grupo,
          })) ?? [],
      })),
    };

    (this.props.id
      ? atividades.update(this.props.id, payload)
      : atividades.create(payload)
    )
      .then(() => {
        this.props.navigate("/atividades");
      })
      .catch((err) => {
        this.setState({
          error: errorToString(err),
          saving: false,
        });
        window.scrollTo(0, 0);
      });
  }

  changePageTab(index: number) {
    this.setState({
      activePage: index,
      activeSlot: -1,
    });
  }

  componentDidUpdate() {
    const pages = [...this.state.pages];
    let changed = false;
    for (const page of pages) {
      if (page.base) {
        const w = page.base.ref.current?.width;
        if (w && page.base.width != w) {
          page.base.width = w;
          changed = true;
        }
        const h = page.base.ref.current?.height;
        if (h && page.base.height != h) {
          page.base.height = h;
          changed = true;
        }
      }
    }
    if (changed) {
      this.setState({ pages });
    }
  }

  onEnterSlot(index: number) {
    this.setState({ activeSlot: index });
  }

  onLeaveSlot() {
    this.setState({ activeSlot: -1 });
  }

  render(): React.ReactNode {
    if (this.state.loadingEntity) {
      return (
        <div className="text-center">
          <Spinner />
        </div>
      );
    }

    const loading =
      this.state.loadingPreview || this.state.loadingMask || this.state.saving;

    return (
      <Form onSubmit={(e) => this.onSubmit(e)}>
        <Titulo text={`${this.props.id ? "Editar" : "Adicionar"} Atividade`} />
        {this.state.error && (
          <Alert color="danger" data-cy="alert">
            {this.state.error}
          </Alert>
        )}
        <FormGroup row>
          <Label for="txtNome" sm={2}>
            Nome:
          </Label>
          <Col sm={10}>
            <Input
              id="txtNome"
              data-cy="nome"
              value={this.state.nome}
              onChange={(e) => this.onNomeChanged(e.target.value)}
              required
              disabled={this.state.saving}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="cmbAno" sm={2}>
            Ano:
          </Label>
          <Col sm={10}>
            <Input
              id="cmbAno"
              data-cy="ano"
              value={this.state.ano.toString()}
              onChange={(e) => this.onAnoChanged(e.target.value)}
              required
              disabled={this.state.saving}
              type="select"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                <option key={i} value={i}>
                  {i}º
                </option>
              ))}
            </Input>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="txtBase" sm={2}>
            PDF da Atividade:
          </Label>
          <Col sm={10}>
            <AtividadesUploadInput
              id="txtBase"
              data-cy="base"
              onUriChanged={(v) => this.onBaseChanged(v)}
              disabled={this.state.loadingPreview || this.state.saving}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="txtMascara" sm={2}>
            PDF da Máscara:
          </Label>
          <Col sm={10}>
            <AtividadesUploadInput
              id="txtMascara"
              data-cy="mascara"
              onUriChanged={(v) => this.onMaskChanged(v)}
              disabled={this.state.loadingMask || this.state.saving}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="txtAudio" sm={2}>
            Áudio (mp3):
          </Label>
          <Col sm={10}>
            <Row>
              <AtividadesUploadInput
                id="txtAudio"
                data-cy="audio"
                format="mp3"
                onUriChanged={(v) => this.onAudioChanged(v)}
                disabled={this.state.loadingPreview || this.state.saving}
              />
            </Row>
            {this.state.audioUri && (
              <Row className="mt-2">
                <audio controls src={this.state.audioUri} />
              </Row>
            )}
          </Col>
        </FormGroup>
        <Row>
          <Col sm={6}>{loading && <Spinner />}</Col>
          <Col sm={6} className="text-end">
            <Button type="submit" data-cy="salvar" disabled={loading}>
              Salvar
            </Button>
          </Col>
        </Row>
        {this.state.pages.length > 0 && (
          <div>
            <Nav tabs>
              {this.state.pages.map((_, idx) => (
                <NavItem key={idx}>
                  <NavLink
                    onClick={() => this.changePageTab(idx)}
                    active={this.state.activePage == idx}
                    style={{ cursor: "pointer" }}
                  >
                    Página {idx + 1}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
            <TabContent activeTab={this.state.activePage}>
              {this.state.pages.map((_, idx) => (
                <TabPane key={idx} tabId={idx}>
                  {this._renderPage(idx, loading)}
                </TabPane>
              ))}
            </TabContent>
          </div>
        )}
      </Form>
    );
  }

  _renderPage(pageIndex: number, loading: boolean): React.ReactNode {
    const { base, mask } = this.state.pages[pageIndex];

    if (!base) {
      return (
        <Alert color="secondary" style={{ marginTop: "0.5em" }}>
          Envie o PDF da atividade.
        </Alert>
      );
    }

    if (!mask) {
      return (
        <Alert color="secondary" style={{ marginTop: "0.5em" }}>
          Envie o PDF da máscara.
        </Alert>
      );
    }

    const px = base.width / mask.width;
    const py = base.height / mask.height;

    return (
      <div>
        <div className="div-preview">
          <img
            ref={base.ref}
            onLoad={() => this.onBaseLoaded(pageIndex)}
            data-cy="previa"
            src={base.uri}
          />
          {mask.slots.map((i, idx) => (
            <div
              key={`${i.x1},${i.y1}`}
              className="div-slot"
              style={{
                left: c(i.x1, px),
                top: c(i.y1, py),
                width: c(i.x2 - i.x1, px),
                height: c(i.y2 - i.y1, py),
              }}
              onMouseEnter={() => this.onEnterSlot(idx)}
              onMouseLeave={() => this.onLeaveSlot()}
            >
              {this.state.activeSlot == idx && (
                <div className="div-selecao-slot">
                  Grupo
                  <div className="div-selecao-slot-content">
                    {groupColors.map((cor, cidx) => (
                      <SlotGrupo
                        key={cor}
                        cor={cor}
                        selected={i.grupo == cidx}
                        onClick={() =>
                          this.onSlotGroupChanged(pageIndex, idx, cidx)
                        }
                      />
                    ))}
                  </div>
                </div>
              )}
              <Input
                tabIndex={mask.slots.length - idx}
                data-cy="slot"
                style={{
                  fontSize: c(0.6 * (i.y2 - i.y1), py),
                  backgroundColor: groupColors[i.grupo],
                }}
                disabled={loading}
                value={i.resposta}
                onChange={(e) =>
                  this.onSlotChanged(pageIndex, idx, e.target.value)
                }
              />
            </div>
          ))}
        </div>
      </div>
    );
  }
}

export function AtividadesForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  return <AtividadesFormImpl navigate={navigate} id={id} />;
}

function c(n: number, p: number) {
  return p ? Math.trunc(n * p).toString() + "px" : 0;
}
